import React, { useState } from "react";
import InputCell from "./InputCell";
import { ChildFormProps, EducationDetails } from "../../../types/Form-types";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import ErrorMessage from "../common-components/ErrorMessage";

const emptyRow = {
  qualification: "",
  duration_from: "",
  duration_to: "",
  university_school: "",
  major_subjects: "",
  percentage_cgpa: "",
};

type EducationRow = typeof emptyRow;

const COLUMNS: {
  label: string;
  field: keyof EducationRow;
  type: "text" | "date";
}[] = [
  { label: "Qualification", field: "qualification", type: "text" },
  { label: "From", field: "duration_from", type: "date" },
  { label: "To", field: "duration_to", type: "date" },
  {
    label: "University/School/Institute",
    field: "university_school",
    type: "text",
  },
  { label: "Major Subjects", field: "major_subjects", type: "text" },
  // { label: "% Marks / CGPA", field: "percentage_cgpa", type: "text" },
];

interface EducationalDetailsProps extends ChildFormProps {
  handleError: (name: any) => void;
}

const EducationalDetails = ({
  formData,
  setFormData,
  errors,
  handleError,
}: EducationalDetailsProps) => {
  // ✅ start with 1 row instead of 5
  const [rows, setRows] = useState<EducationRow[]>([{ ...emptyRow }]);

  const getValue = (e: any): string => {
    if (typeof e === "string") return e;
    return e?.target?.value ?? "";
  };

  const handleChange = (
    index: number,
    field: keyof EducationRow,
    value: string,
  ) => {
    setRows((prevRows) => {
      const updated = prevRows.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      );

      const mapped = updated
        .filter((row) => Object.values(row).some((v) => v !== ""))
        .map((row) => ({
          qualification: row.qualification,
          duration_from: row.duration_from,
          duration_to: row.duration_to,
          university_school: row.university_school,
          major_subjects: row.major_subjects,
          percentage_cgpa: row.percentage_cgpa,
        }));

      setFormData((prev: any) => ({
        ...prev,
        education_details: mapped,
        _education_rows_raw: updated,
      }));

      return updated;
    });

    handleError(`education_details.${index}.${field}`);
  };

  const handleDateChange = (
    index: number,
    field: keyof EducationRow,
    dateString: string,
  ) => {
    const formatted = dateString
      ? dayjs(dateString, "MM/YY").format("MM/YY")
      : "";
    handleChange(index, field, formatted);
  };

  // ✅ add one empty row
  const handleAddRow = () => {
    setRows((prev) => [...prev, { ...emptyRow }]);
  };

  // ✅ remove row by index
  const handleRemoveRow = (index: number) => {
    setRows((prevRows) => {
      const updated = prevRows.filter((_, i) => i !== index);

      const mapped = updated
        .filter((row) => Object.values(row).some((v) => v !== ""))
        .map((row) => ({ ...row }));

      setFormData((prev: any) => ({
        ...prev,
        education_details: mapped,
        _education_rows_raw: updated,
      }));

      return updated;
    });
  };

  return (
    <div className="pt-3">
      <div className="mb-3 d-sm-flex align-items-center justify-content-between">
        <div>
          <p className="form-section-title mb-1">Educational Details</p>
          <p
            className="text-muted fst-italic m-0"
            style={{ fontSize: "0.78rem" }}
          >
            (X Standard/Equivalent onwards – In reverse chronological order,
            i.e. last qualification first)
          </p>
        </div>
        {/* ✅ Add Row button */}
        <div className="text-end ">
          <button
            type="button"
            onClick={handleAddRow}
            style={{
              background: "none",
              border: "1px dashed #fd7e14",
              color: "#fd7e14",
              borderRadius: "4px",
              padding: "4px 12px",
              fontSize: "0.82rem",
              cursor: "pointer",
            }}
          >
            + Add Row
          </button>
        </div>
      </div>
      <div className="table-responsive mb-2">
        <table className="form-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ minWidth: "160px" }}>
                Qualification
              </th>
              <th colSpan={2}>Duration (mm/yy)</th>
              <th rowSpan={2}>University/School/Institute</th>
              <th rowSpan={2} style={{ minWidth: "100px" }}>
                Major Subjects
              </th>
              <th rowSpan={2}>% Marks / CGPA</th>
              {/* ✅ remove column header — only show if more than 1 row */}
              {rows.length > 1 && <th rowSpan={2}></th>}
            </tr>
            <tr>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>
          <tbody>
            {rows?.map((row, i) => (
              <tr key={i}>
                {COLUMNS?.map((col) => {
                  const errorMsg =
                    errors?.education_details?.[i]?.[
                      col.field as keyof EducationDetails
                    ];
                  return (
                    <React.Fragment key={col.field}>
                      {col.type === "date" ? (
                        <td
                          style={{
                            padding: 0,
                            minWidth: "150px",
                            maxWidth: "150px",
                            width: "150px",
                          }}
                          className="px-2 py-1"
                        >
                          <DatePicker
                            name={col.field}
                            style={{ maxWidth: "100px", marginTop: "-0.5rem" }}
                            className="p-0 ps-1 form-control datetimepicker d-inline"
                            format={{ format: "MM/YY", type: "mask" }}
                            placeholder="MM/YY"
                            value={
                              row[col.field]
                                ? dayjs(row[col.field], "MM/YY")
                                : null
                            }
                            disabledDate={(current) => {
                              if (col.field == "duration_to") {
                                const start = dayjs(row.duration_from, "MM/YY");
                                return (
                                  current && current.isBefore(start, "month")
                                );
                              } else {
                                return (
                                  current && current.isAfter(dayjs(), "month")
                                );
                              }
                            }}
                            disabled={
                              col.field == "duration_to" &&
                              !row["duration_from"]
                            }
                            onChange={(
                              date: any,
                              dateString: string | string[],
                            ) => {
                              const value = date
                                ? typeof dateString === "string"
                                  ? dateString
                                  : dateString[0]
                                : "";
                              handleDateChange(i, col.field, value);
                            }}
                          />
                          <ErrorMessage message={errorMsg} />
                        </td>
                      ) : (
                        <InputCell
                          name={col.field}
                          value={row[col.field]}
                          handleChange={(e) =>
                            handleChange(i, col.field, getValue(e))
                          }
                          errorMessage={errorMsg}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                <td colSpan={1} style={{ minWidth: "200px" }}>
                  <div className="input-group mb-3">
                    <input
                      name="percentage_cgpa"
                      type="text"
                      inputMode="decimal"
                      // max={100}
                      step="0.1"
                      className="form-control border"
                      placeholder="eg: 9.5 or 89"
                      aria-label="Text input with dropdown button"
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, "");
                        handleChange(i, "percentage_cgpa", val);
                      }}
                    />
                    <select
                      className="form-select flex-grow-0 ps-2 pe-1"
                      style={{ width: "85px", fontSize: "0.75rem" }}
                    >
                      {["CGPA", "% age"].map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage
                      message={
                        errors?.education_details?.[i]?.["percentage_cgpa"]
                      }
                    />
                  </div>
                </td>

                {/* ✅ remove button — only if more than 1 row */}
                {rows.length > 1 && (
                  <td style={{ textAlign: "center", width: 40 }}>
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(i)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "1rem",
                        padding: 0,
                      }}
                      title="Remove row"
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EducationalDetails;
